import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { Milestone } from '@/features/projects/types';
import { MilestoneProgress } from './MilestoneProgress';

interface WaitlistSuccessCardProps {
    position: number;
    totalWaitlist: number;
    referralLink: string;
    referralsCount: number;
    primaryColor: string;
    successMessage?: string;
    milestones?: Milestone[];
    gamificationEnabled?: boolean;
    showQueuePosition?: boolean;
    showReferralAfterSubmit?: boolean;
}

export function WaitlistSuccessCard({
    position,
    totalWaitlist,
    referralLink,
    referralsCount,
    primaryColor,
    successMessage = "Want to cut the line?",
    milestones,
    gamificationEnabled,
    showQueuePosition = true,
    showReferralAfterSubmit = true,
}: WaitlistSuccessCardProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const shareUrl = encodeURIComponent(referralLink);
    const shareText = encodeURIComponent("I just joined the waitlist! You should too. 🚀");

    const socialLinks = [
        {
            name: 'Twitter',
            icon: 'Twitter' as const,
            url: `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`,
            bgColor: 'bg-[#1DA1F2]',
            textColor: 'text-white'
        },
        {
            name: 'LinkedIn',
            icon: 'Linkedin' as const,
            url: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
            bgColor: 'bg-[#0077B5]',
            textColor: 'text-white'
        },
        {
            name: 'WhatsApp',
            icon: 'ChatBubbleLeftEllipsisIcon' as const,
            url: `https://wa.me/?text=${shareText}%20${shareUrl}`,
            bgColor: 'bg-[#25D366]',
            textColor: 'text-white'
        }
    ];

    return (
        <div className="w-full max-w-md mx-auto bg-white rounded-3xl shadow-[0_20px_40px_-12px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden animate-in fade-in zoom-in duration-500">
            {/* Top Section: Position */}
            <div className="pt-10 pb-8 px-8 text-center bg-white relative">
                {/* Success Icon */}
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-green-100 animate-[bounce_1s_ease-out_1]">
                    <Icon name="CheckCircleIcon" className="w-8 h-8 text-green-600" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-6 tracking-tight">You&apos;re on the waitlist!</h3>

                {/* Position Card */}
                {showQueuePosition && (
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-gray-50 rounded-2xl border border-gray-200/60 shadow-inner">
                        <span className="text-gray-500 text-sm font-medium">Your current position:</span>
                        <span className="text-2xl font-bold text-gray-900 tracking-tight">#{position}</span>
                    </div>
                )}
            </div>

            {showReferralAfterSubmit && (
                <>
                    {/* Divider */}
                    <div className="h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>

                    {/* Bottom Section: Referral Action */}
                    <div className="p-8 bg-gray-50/30">
                        <div className="text-center mb-8">
                            <div className="inline-block">
                                <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2 justify-center">
                                    {successMessage}
                                </h4>
                            </div>

                            <p className="text-sm text-gray-500 mt-2 leading-relaxed max-w-xs mx-auto">
                                Refer your friends and move up the list!
                                <span className="block text-xs text-gray-400 mt-1 font-medium">
                                    (Get early access for every referral)
                                </span>
                            </p>
                        </div>

                        {/* Social Share Buttons */}
                        <div className="flex justify-center flex-wrap gap-3 mb-8">
                            {socialLinks.map((social) => (
                                <a
                                    key={social.name}
                                    href={social.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gray-200/50 ${social.bgColor} ${social.textColor}`}
                                    style={{ minWidth: '100px' }}
                                >
                                    <span>{social.name}</span>
                                </a>
                            ))}
                            <button
                                onClick={handleCopy}
                                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all border shadow-sm hover:shadow-md ${copied ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                                style={{ minWidth: '100px' }}
                            >
                                {copied ? 'Copied!' : 'Copy Link'}
                            </button>
                        </div>

                        {/* Referral Link */}
                        <div className="relative flex items-center mb-8 group">
                            <div className="w-full bg-gray-100/50 rounded-xl px-4 py-3.5 text-sm text-gray-600 font-mono border border-gray-200 group-hover:border-gray-300 transition-colors truncate pr-12 text-center select-all">
                                {referralLink}
                            </div>
                            <div className="absolute right-3 text-gray-400 pointer-events-none">
                                <Icon name="DocumentDuplicateIcon" size={16} />
                            </div>
                        </div>

                        {/* Referral Count */}
                        <div className="text-center">
                            <p className="text-sm text-gray-500">
                                You have referred <span className="font-bold text-gray-900 transition-colors" style={{ color: primaryColor }}>{referralsCount} friends</span>
                            </p>
                        </div>

                        {/* Milestone Progress */}
                        {gamificationEnabled && milestones && milestones.length > 0 && (
                            <MilestoneProgress
                                milestones={milestones}
                                referralsCount={referralsCount}
                                primaryColor={primaryColor}
                    />
                )}
            </div>
        </>
        )}
        </div>
    );
}
